/**
 * React hooks for API integration
 * Performance optimized with proper cleanup and memoization
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService, ApiResponse } from '@/services/api';
import { FrontendReferral, backendToFrontendReferral, frontendToBackendReferral } from '@/services/dataTransform';

export interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for managing referrals with backend integration
 * @param filters - Optional filters for referrals
 * @param enabled - Whether to enable fetching (default: true). Set to false to skip API calls.
 */
export function useReferrals(filters?: { program_id?: number; status?: string }, enabled: boolean = true): UseApiState<FrontendReferral[]> & {
  createReferral: (referral: FrontendReferral) => Promise<boolean>;
  updateReferralStatus: (id: string | number, status: string) => Promise<boolean>;
} {
  const [data, setData] = useState<FrontendReferral[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchReferrals = useCallback(async () => {
    // Skip fetch if not enabled (e.g., user not authenticated)
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getReferrals(filters);

      if (response.success && response.data) {
        // Backend returns {referrals: []}, so we need to access the referrals array
        const referralsArray = response.data.referrals || response.data;
        const frontendReferrals = Array.isArray(referralsArray)
          ? referralsArray.map(backendToFrontendReferral)
          : [];
        setData(frontendReferrals);
      } else {
        setError(response.error || 'Failed to fetch referrals');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [filters, enabled]);

  const createReferral = useCallback(async (referral: FrontendReferral): Promise<boolean> => {
    if (!enabled) return false;

    try {
      setError(null);

      const backendReferral = frontendToBackendReferral(referral);
      const response = await apiService.createReferral(backendReferral);

      if (response.success && response.data) {
        const newFrontendReferral = backendToFrontendReferral(response.data);
        setData(prev => [newFrontendReferral, ...prev]);
        return true;
      } else {
        setError(response.error || 'Failed to create referral');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    }
  }, [enabled]);

  const updateReferralStatus = useCallback(async (id: string | number, status: string): Promise<boolean> => {
    if (!enabled) return false;

    try {
      setError(null);

      // Update local state immediately for better UX
      setData(prev => prev.map(ref =>
        ref.id === id ? { ...ref, status: status as any } : ref
      ));

      // In a real app, you'd make an API call here to update the backend
      // For now, we'll just update the local state
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    }
  }, [enabled]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  return {
    data,
    loading,
    error,
    refetch: fetchReferrals,
    createReferral,
    updateReferralStatus,
  };
}

/**
 * Hook for managing CBOs
 * @param enabled - Whether to enable fetching (default: true). Set to false to skip API calls.
 */
export function useCBOs(enabled: boolean = true): UseApiState<any[]> {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true);

  const fetchCBOs = useCallback(async () => {
    // Skip fetch if not enabled (e.g., user not authenticated)
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getCBOs();

      // Only update state if still mounted
      if (!isMountedRef.current) return;

      if (response.success && response.data) {
        // Backend returns {cbos: []}, so we need to access the cbos array
        const cbosArray = response.data.cbos || response.data;
        setData(Array.isArray(cbosArray) ? cbosArray : []);
      } else {
        setError(response.error || 'Failed to fetch CBOs');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchCBOs();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchCBOs]);

  return {
    data,
    loading,
    error,
    refetch: fetchCBOs,
  };
}

/**
 * Hook for managing metrics
 * @param enabled - Whether to enable fetching (default: true). Set to false to skip API calls.
 */
export function useMetrics(enabled: boolean = true): UseApiState<any> {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    // Skip fetch if not enabled (e.g., user not authenticated)
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getMetrics();

      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to fetch metrics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    data,
    loading,
    error,
    refetch: fetchMetrics,
  };
}

/**
 * Hook for health check
 */
export function useHealthCheck(): UseApiState<any> {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.healthCheck();
      
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Health check failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    data,
    loading,
    error,
    refetch: checkHealth,
  };
}

/**
 * Hook for provider count from Provider Directory API
 */
export function useProviderCount(): UseApiState<number> {
  const [data, setData] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProviderCount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const providerApiUrl = import.meta.env.VITE_PROVIDER_API_URL ?? 'https://provider-directory-api.onrender.com/api';
      const baseUrl = providerApiUrl.replace('/api', ''); // Remove /api to get base URL
      const response = await fetch(`${baseUrl}/health`);

      if (response.ok) {
        const data = await response.json();
        setData(data.providers_count || 0);
      } else {
        setError('Failed to fetch provider count');
        setData(0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviderCount();
  }, [fetchProviderCount]);

  return {
    data,
    loading,
    error,
    refetch: fetchProviderCount,
  };
}
